import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import {
  PlayArrow,
  BarChart,
  ExpandMore,
  Share,
  Lock,
  Settings,
  Tune,
  Description,
  Close,
} from "@mui/icons-material";
import { FormattedMessage } from "react-intl";

const HelpCenter = ({ locale = 'en' }) => {
  const [blogModal, setBlogModal] = useState({ open: false, post: null });
  const surveyEngineFlow = [
    { icon: <PlayArrow />, title: "home.step1_title", description: "home.step1_description" },
    { icon: <Share />, title: "home.step2_title", description: "home.step2_description" },
    { icon: <Lock />, title: "home.step3_title", description: "home.step3_description" },
    { icon: <Settings />, title: "home.step4_title", description: "home.step4_description" },
    { icon: <Tune />, title: "home.step5_title", description: "home.step5_description" },
    { icon: <Description />, title: "home.step6_title", description: "home.step6_description" },
    { icon: <BarChart />, title: "home.step7_title", description: "home.step7_description" },
  ];

  const faqs = [
    { question: "home.faq1_question", answer: "home.faq1_answer" },
    { question: "home.faq2_question", answer: "home.faq2_answer" },
    { question: "home.faq3_question", answer: "home.faq3_answer" },
  ];

  const blogPosts = [
    {
      id: 1,
      title: "help.blog1_title",
      date: "2025-03-10",
      excerpt: "home.blog1_excerpt",
      author: "help.blog1_author",
      readTime: "help.blog1_read_time",
      content: {
        introduction: "help.blog1_introduction",
        tips: [
          { title: "help.blog1_tip1_title", content: "help.blog1_tip1_content" },
          { title: "help.blog1_tip2_title", content: "help.blog1_tip2_content" },
          { title: "help.blog1_tip3_title", content: "help.blog1_tip3_content" },
          { title: "help.blog1_tip4_title", content: "help.blog1_tip4_content" },
          { title: "help.blog1_tip5_title", content: "help.blog1_tip5_content" }
        ],
        conclusion: "help.blog1_conclusion"
      }
    },
    {
      id: 2,
      title: "help.blog2_title",
      date: "2025-03-05",
      excerpt: "home.blog2_excerpt",
      author: "help.blog2_author",
      readTime: "help.blog2_read_time",
      content: {
        introduction: "help.blog2_introduction",
        sections: [
          { title: "help.blog2_section1_title", content: "help.blog2_section1_content" },
          { title: "help.blog2_section2_title", content: "help.blog2_section2_content" },
          { title: "help.blog2_section3_title", content: "help.blog2_section3_content" },
          { title: "help.blog2_section4_title", content: "help.blog2_section4_content" },
          { title: "help.blog2_section5_title", content: "help.blog2_section5_content" }
        ],
        conclusion: "help.blog2_conclusion"
      }
    }
  ];

  const handleOpenBlogModal = (post) => {
    setBlogModal({ open: true, post });
  };

  const handleCloseBlogModal = () => {
    setBlogModal({ open: false, post: null });
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}

      {/* Latest News and FAQs - Two Column Layout */}
      <Grid container spacing={4} sx={{ mb: 6 }}>
        {/* Latest News Column */}
        <Grid item xs={12} md={6}>
          <Box sx={{ height: "100%" }}>
            <Typography variant="h4" fontWeight="bold" gutterBottom color="#00438E" sx={{ textAlign: "center", mb: 4 }}>
              <FormattedMessage id="home.latest_news" />
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {blogPosts.map((post, index) => (
                <Card key={index} sx={{ boxShadow: 3, borderLeft: "4px solid #00438E" }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      <FormattedMessage id={post.title} />
                    </Typography>
                    <Typography variant="caption" display="block" color="textSecondary" sx={{ mb: 1 }}>
                      {post.date} • <FormattedMessage id={post.author} /> • <FormattedMessage id={post.readTime} />
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                      <FormattedMessage id={post.excerpt} />
                    </Typography>
                    <Button size="small" sx={{ color: "#00438E" }} onClick={() => handleOpenBlogModal(post)}>
                      <FormattedMessage id="home.read_more" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Box>
        </Grid>

        {/* FAQs Column */}
        <Grid item xs={12} md={6}>
          <Box sx={{ height: "100%" }}>
            <Typography variant="h4" fontWeight="bold" gutterBottom color="#00438E" sx={{ textAlign: "center", mb: 4 }}>
              <FormattedMessage id="home.faqs" />
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {faqs.map((faq, index) => (
                <Accordion key={index} sx={{ boxShadow: 3, borderLeft: "4px solid #00438E" }}>
                  <AccordionSummary expandIcon={<ExpandMore sx={{ color: "#00438E" }} />}>
                    <Typography variant="h6">
                      <FormattedMessage id={faq.question} />
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body2">
                      <FormattedMessage id={faq.answer} />
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* Survey Engine Guide */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ textAlign: "center", color: "#00438E", mb: 4 }}>
          <FormattedMessage id="home.survey_engine_title" />
        </Typography>
        <Card
          sx={{
            boxShadow: "0 8px 32px rgba(0, 67, 142, 0.12)",
            borderRadius: "16px",
            background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
            border: "1px solid rgba(0, 67, 142, 0.1)",
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Typography variant="body1" paragraph sx={{ textAlign: "center", mb: 4, color: "textSecondary", fontSize: "1.1rem", lineHeight: 1.6 }}>
              <FormattedMessage id="home.survey_engine_description" />
            </Typography>
            <Box sx={{ position: "relative", maxWidth: 800, mx: "auto" }}>
              {surveyEngineFlow.map((step, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mb: 4,
                    position: "relative",
                    borderRadius: 3,
                    p: 2,
                    transition: "all 0.3s ease-in-out",
                    "&:hover": {
                      backgroundColor: "rgba(0, 67, 142, 0.02)",
                      transform: "translateX(8px)",
                    },
                    "&:before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      bottom: -40,
                      left: "50%",
                      width: "4px",
                      background: `linear-gradient(to bottom, #4A87C8, #1976d2)`,
                      zIndex: 0,
                      borderRadius: "2px",
                    },
                    "&:last-child:before": { display: "none" },

                    // Enhanced hover interactions
                    ".timeline-icon": {
                      transition: "all 0.3s ease-in-out",
                    },
                    "&:hover .timeline-icon": {
                      transform: "translateY(-3px) scale(1.08)",
                      boxShadow: "0 8px 24px rgba(74, 135, 200, 0.3)"
                    },
                    "&:hover .timeline-title": {
                      color: "#00438E",
                      fontWeight: 700,
                    },
                  }}
                >
                  <Box sx={{ flex: 1, textAlign: "right", pr: 4 }}>
                    {index % 2 === 0 ? (
                      <>
                        <Typography className="timeline-title" variant="h6" fontWeight="bold" color="text.primary">
                          <FormattedMessage id={step.title} />
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          <FormattedMessage id={step.description} />
                        </Typography>
                      </>
                    ) : null}
                  </Box>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      backgroundColor: "primary.main",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 1,
                      boxShadow: 2,
                      position: "relative",
                      overflow: "visible",
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        inset: -6,
                        borderRadius: "50%",
                        backgroundColor: (theme) => theme.palette.primary.main,
                        opacity: 0.12,
                        filter: "blur(4px)",
                        transform: "scale(0.92)",
                        transition: "opacity 220ms ease, transform 220ms ease",
                        zIndex: -1,
                      },
                      "&:hover::after": {
                        opacity: 0.22,
                        transform: "scale(1.05)",
                      },
                    }}
                    className="timeline-icon"
                  >
                    <Box sx={{ color: "white", "& svg": { transition: "transform 180ms ease" }, "&:hover svg": { transform: "rotate(8deg) scale(1.05)" } }}>
                      {step.icon}
                    </Box>
                  </Box>
                  <Box sx={{ flex: 1, textAlign: "left", pl: 4 }}>
                    {index % 2 !== 0 ? (
                      <>
                        <Typography className="timeline-title" variant="h6" fontWeight="bold" color="text.primary">
                          <FormattedMessage id={step.title} />
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          <FormattedMessage id={step.description} />
                        </Typography>
                      </>
                    ) : null}
                  </Box>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Contact Support */}
      <Box
        sx={{
          backgroundColor: "#00438E",
          color: "white",
          p: 4,
          borderRadius: 2,
          textAlign: "center",
          boxShadow: 3,
        }}
      >
        <Typography variant="h5" gutterBottom>
          <FormattedMessage id="help.contact_title" defaultMessage="Need More Help?" />
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          <FormattedMessage id="help.contact_description" defaultMessage="Can't find what you're looking for? Our support team is here to help." />
        </Typography>
        <Button
          variant="contained"
          sx={{
            backgroundColor: "white",
            color: "#00438E",
            "&:hover": { backgroundColor: "#f0f0f0" },
            px: 4,
            py: 1.5,
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          <FormattedMessage id="help.contact_support" defaultMessage="Contact Support" />
        </Button>
      </Box>

      {/* Blog Modal */}
      <Dialog
        open={blogModal.open}
        onClose={handleCloseBlogModal}
        maxWidth="md"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: '16px',
            maxHeight: '90vh',
          }
        }}
      >
        {blogModal.post && (
          <>
            <DialogTitle
              sx={{
                backgroundColor: '#00438E',
                color: 'white',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                pb: 1,
              }}
            >
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
                  <FormattedMessage id={blogModal.post.title} />
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    <FormattedMessage id="help.by_author" defaultMessage="By" /> <FormattedMessage id={blogModal.post.author} />
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    •
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    <FormattedMessage id={blogModal.post.readTime} />
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    •
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    {blogModal.post.date}
                  </Typography>
                </Box>
              </Box>
              <IconButton
                onClick={handleCloseBlogModal}
                sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' } }}
              >
                <Close />
              </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 4 }}>
              <Box>
                {/* Introduction */}
                <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7, fontSize: '1.1rem' }}>
                  <FormattedMessage id={blogModal.post.content.introduction} />
                </Typography>

                {/* Main Content */}
                <Box sx={{ mb: 4 }}>
                  {blogModal.post.content[blogModal.post.id === 1 ? 'tips' : 'sections'].map((item, index) => (
                    <Box key={index} sx={{ mb: 3 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: '#00438E',
                          mb: 1.5,
                          fontSize: '1.1rem'
                        }}
                      >
                        {index + 1}. <FormattedMessage id={item.title} />
                      </Typography>
                      <Typography variant="body1" sx={{ lineHeight: 1.6, pl: 1 }}>
                        <FormattedMessage id={item.content} />
                      </Typography>
                    </Box>
                  ))}
                </Box>

                {/* Conclusion */}
                <Box sx={{
                  p: 3,
                  backgroundColor: 'rgba(0, 67, 142, 0.05)',
                  borderRadius: '8px',
                  borderLeft: '4px solid #00438E'
                }}>
                  <Typography variant="body1" sx={{ lineHeight: 1.6, fontStyle: 'italic' }}>
                    <FormattedMessage id={blogModal.post.content.conclusion} />
                  </Typography>
                </Box>
              </Box>
            </DialogContent>

            <DialogActions sx={{
              p: 3,
              pt: 2,
              borderTop: '1px solid #e0e0e0',
              backgroundColor: '#f8f9fa'
            }}>
              <Button
                onClick={handleCloseBlogModal}
                variant="contained"
                sx={{
                  backgroundColor: '#00438E',
                  '&:hover': { backgroundColor: '#003A75' },
                  borderRadius: '8px',
                  px: 3
                }}
              >
                <FormattedMessage id="home.close" defaultMessage="Close" />
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Container>
  );
};

export default HelpCenter;
